/**
 * Phone number validation and formatting utilities
 */

/**
 * Country-specific phone number validation rules
 * Format: [minDigits, maxDigits, errorMessage]
 */
const COUNTRY_PHONE_RULES: Record<string, { min: number; max: number; example: string }> = {
  "+1": { min: 10, max: 10, example: "415 555 2671" }, // US/Canada
  "+44": { min: 10, max: 10, example: "7485 647218" }, // UK (mobile: 07xxx xxxxxx without leading 0)
  "+61": { min: 9, max: 9, example: "2 1234 5678" }, // Australia
  "+49": { min: 10, max: 11, example: "30 12345678" }, // Germany
  "+33": { min: 9, max: 9, example: "1 23 45 67 89" }, // France
  "+39": { min: 9, max: 10, example: "02 1234 5678" }, // Italy
  "+34": { min: 9, max: 9, example: "912 345 678" }, // Spain
  "+31": { min: 9, max: 9, example: "20 123 4567" }, // Netherlands
  "+32": { min: 9, max: 9, example: "2 123 45 67" }, // Belgium
  "+41": { min: 9, max: 9, example: "21 123 45 67" }, // Switzerland
  "+43": { min: 10, max: 13, example: "1 234 5678" }, // Austria
  "+46": { min: 9, max: 9, example: "8 123 456 78" }, // Sweden
  "+47": { min: 8, max: 8, example: "12 34 56 78" }, // Norway
  "+45": { min: 8, max: 8, example: "12 34 56 78" }, // Denmark
  "+358": { min: 9, max: 10, example: "9 1234 5678" }, // Finland
  "+353": { min: 9, max: 9, example: "1 234 5678" }, // Ireland
  "+351": { min: 9, max: 9, example: "21 123 4567" }, // Portugal
  "+30": { min: 10, max: 10, example: "21 1234 5678" }, // Greece
  "+48": { min: 9, max: 9, example: "12 345 67 89" }, // Poland
  "+420": { min: 9, max: 9, example: "212 345 678" }, // Czech Republic
  "+36": { min: 9, max: 9, example: "1 234 5678" }, // Hungary
  "+40": { min: 9, max: 9, example: "21 123 4567" }, // Romania
  "+359": { min: 9, max: 9, example: "2 123 4567" }, // Bulgaria
  "+385": { min: 8, max: 9, example: "1 234 5678" }, // Croatia
  "+421": { min: 9, max: 9, example: "2 1234 5678" }, // Slovakia
  "+386": { min: 8, max: 8, example: "1 234 5678" }, // Slovenia
  "+372": { min: 7, max: 8, example: "123 4567" }, // Estonia
  "+371": { min: 8, max: 8, example: "12 345 678" }, // Latvia
  "+370": { min: 8, max: 8, example: "5 123 4567" }, // Lithuania
  "+52": { min: 10, max: 10, example: "55 1234 5678" }, // Mexico
  "+55": { min: 10, max: 11, example: "11 91234 5678" }, // Brazil
  "+54": { min: 10, max: 10, example: "11 1234 5678" }, // Argentina
  "+56": { min: 9, max: 9, example: "2 1234 5678" }, // Chile
  "+57": { min: 10, max: 10, example: "1 234 5678" }, // Colombia
  "+51": { min: 9, max: 9, example: "1 234 5678" }, // Peru
  "+58": { min: 10, max: 10, example: "212 123 4567" }, // Venezuela
  "+593": { min: 9, max: 9, example: "2 123 4567" }, // Ecuador
  "+591": { min: 8, max: 8, example: "2 123 4567" }, // Bolivia
  "+595": { min: 9, max: 9, example: "21 123 456" }, // Paraguay
  "+598": { min: 8, max: 8, example: "2 123 4567" }, // Uruguay
  "+81": { min: 10, max: 10, example: "90 1234 5678" }, // Japan
  "+86": { min: 11, max: 11, example: "138 0013 8000" }, // China
  "+82": { min: 9, max: 10, example: "10 1234 5678" }, // South Korea
  "+91": { min: 10, max: 10, example: "91234 56789" }, // India
  "+65": { min: 8, max: 8, example: "9123 4567" }, // Singapore
  "+60": { min: 9, max: 10, example: "12 345 6789" }, // Malaysia
  "+66": { min: 9, max: 9, example: "81 234 5678" }, // Thailand
  "+63": { min: 10, max: 10, example: "917 123 4567" }, // Philippines
  "+62": { min: 9, max: 11, example: "812 345 6789" }, // Indonesia
  "+84": { min: 9, max: 10, example: "91 234 5678" }, // Vietnam
  "+64": { min: 8, max: 10, example: "21 123 4567" }, // New Zealand
  "+27": { min: 9, max: 9, example: "12 345 6789" }, // South Africa
  "+20": { min: 10, max: 10, example: "2 1234 5678" }, // Egypt
  "+234": { min: 10, max: 10, example: "802 123 4567" }, // Nigeria
  "+254": { min: 9, max: 9, example: "712 123456" }, // Kenya
  "+233": { min: 9, max: 9, example: "24 123 4567" }, // Ghana
  "+972": { min: 9, max: 9, example: "2 123 4567" }, // Israel
  "+971": { min: 9, max: 9, example: "50 123 4567" }, // UAE
  "+966": { min: 9, max: 9, example: "50 123 4567" }, // Saudi Arabia
  "+90": { min: 10, max: 10, example: "212 123 4567" }, // Turkey
  "+7": { min: 10, max: 10, example: "912 345 6789" }, // Russia
  "+380": { min: 9, max: 9, example: "50 123 4567" }, // Ukraine
};

/** Default placeholder when country has no rule (digits only, no spaces). */
const DEFAULT_PHONE_EXAMPLE = "123 456 7890";

/**
 * Get a country-appropriate example phone number (national format, without country code)
 * for use as placeholder or in error messages. Uses the same examples as validation.
 */
export function getPhoneExample(countryCode: string): string {
  const rules = COUNTRY_PHONE_RULES[countryCode];
  return rules?.example ?? DEFAULT_PHONE_EXAMPLE;
}

/**
 * Normalize phone number to E.164 format
 * E.164 format: +[country code][number] (e.g., +14155552671)
 * 
 * @param phone - Phone number to normalize (can include spaces, dashes, etc.)
 * @param countryCode - Optional country dial code (e.g., "+1"). If provided, will be prepended if phone doesn't start with +
 */
export function normalizePhoneNumber(phone: string, countryCode?: string): string {
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, "");
  
  // If it already starts with +, use it as-is (user provided full number)
  if (cleaned.startsWith("+")) {
    return cleaned;
  }
  
  // If country code is provided, prepend it
  if (countryCode) {
    // Remove + from country code if present
    const dialCode = countryCode.replace(/^\+/, "");
    // Combine country code with cleaned number
    return `+${dialCode}${cleaned}`;
  }
  
  // Fallback: try to detect or default to US/Canada (+1)
  // If it starts with 1 and has 11 digits, it's already US format
  if (cleaned.startsWith("1") && cleaned.length === 11) {
    return "+" + cleaned;
  } else if (cleaned.length === 10) {
    // Assume US number, add +1
    return "+1" + cleaned;
  } else {
    // Try to detect country code or default to +1
    return "+" + cleaned;
  }
}

/**
 * Validate phone number format
 */
export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  normalized?: string;
}

export function validatePhoneNumber(
  phone: string,
  countryCode?: string
): PhoneValidationResult {
  if (!phone || typeof phone !== "string") {
    return {
      isValid: false,
      error: "Phone number is required",
    };
  }

  // Normalize the phone number (accepts numbers with or without spaces)
  const normalized = normalizePhoneNumber(phone.trim(), countryCode);

  // E.164 format validation: + followed by 1-15 digits
  const e164Pattern = /^\+[1-9]\d{1,14}$/;
  
  if (!e164Pattern.test(normalized)) {
    return {
      isValid: false,
      error: "Please enter a valid phone number",
    };
  }

  // Check minimum length (country code + at least 4 digits)
  if (normalized.length < 8) {
    return {
      isValid: false,
      error: "Phone number is too short",
    };
  }

  // Check maximum length (E.164 max is 15 digits after +)
  if (normalized.length > 16) {
    return {
      isValid: false,
      error: "Phone number is too long",
    };
  }

  // Country-specific validation if country code is provided
  if (countryCode) {
    const rules = COUNTRY_PHONE_RULES[countryCode];
    if (rules) {
      // Extract the number part (without country code)
      const dialCode = countryCode.replace(/^\+/, "");
      const numberPart = normalized.substring(dialCode.length + 1); // +1 for the +
      const digitCount = numberPart.length;

      if (digitCount < rules.min) {
        return {
          isValid: false,
          error: `Phone number must have at least ${rules.min} digits. Example: ${rules.example}`,
        };
      }

      if (digitCount > rules.max) {
        return {
          isValid: false,
          error: `Phone number must have at most ${rules.max} digits. Example: ${rules.example}`,
        };
      }
    }
  }

  return {
    isValid: true,
    normalized,
  };
}

/**
 * Format phone number for display
 * Converts +14155552671 to +1 (415) 555-2671
 */
export function formatPhoneNumberForDisplay(phone: string): string {
  const normalized = normalizePhoneNumber(phone);
  
  // US/Canada format: +1 (415) 555-2671
  if (normalized.startsWith("+1") && normalized.length === 12) {
    const areaCode = normalized.substring(2, 5);
    const exchange = normalized.substring(5, 8);
    const number = normalized.substring(8, 12);
    return `+1 (${areaCode}) ${exchange}-${number}`;
  }
  
  // For other countries, just return normalized with spaces
  if (normalized.length > 4) {
    return normalized.replace(/(\+?\d{1,3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4");
  }
  
  return normalized;
}

/**
 * Mask phone number for display (privacy)
 * +14155552671 -> +1 (415) ***-2671
 */
export function maskPhoneNumber(phone: string): string {
  const normalized = normalizePhoneNumber(phone);
  
  if (normalized.startsWith("+1") && normalized.length === 12) {
    const areaCode = normalized.substring(2, 5);
    const lastFour = normalized.substring(8, 12);
    return `+1 (${areaCode}) ***-${lastFour}`;
  }
  
  // For other countries, show first 3 and last 3 digits
  if (normalized.length > 6) {
    const prefix = normalized.substring(0, 4);
    const suffix = normalized.substring(normalized.length - 3);
    return `${prefix}***${suffix}`;
  }
  
  return "***";
}

/**
 * Format UK phone number in skeleton format as user types
 * Formats to: XXXX XXXXXX (e.g., "7647 647288")
 * UK mobile numbers are typically 07XXX XXXXXX (11 digits), but when country code is already selected,
 * we only need the 10 digits after +44, formatted as XXXX XXXXXX
 * @param value - Raw input value (digits only)
 * @returns Formatted phone number with spaces
 */
export function formatUKPhoneSkeleton(value: string): string {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");
  
  // Limit to 10 digits (UK phone number length after country code)
  const limited = digits.slice(0, 10);
  
  // Format as XXXX XXXXXX (4 digits, space, 6 digits)
  if (limited.length <= 4) {
    return limited;
  } else {
    return `${limited.slice(0, 4)} ${limited.slice(4)}`;
  }
}

/**
 * Get digit grouping for a country from its example string.
 * e.g. "7485 647218" -> [4, 6], "415 555 2671" -> [3, 3, 4]
 */
function getGroupingForCountry(countryCode: string): number[] {
  const rules = COUNTRY_PHONE_RULES[countryCode];
  if (!rules?.example) return [3, 3, 4]; // default US-style
  const parts = rules.example.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return [3, 3, 4];
  return parts.map((p) => p.length);
}

/**
 * Format phone input as the user types, using the country's number structure.
 * e.g. UK: "7784163824" -> "7784 163824", US: "4155552671" -> "415 555 2671"
 * Use in onChange so digits are grouped with spaces per country code.
 */
export function formatPhoneInput(value: string, countryCode: string): string {
  const digits = value.replace(/\D/g, "");
  const rules = COUNTRY_PHONE_RULES[countryCode];
  const maxLen = rules?.max ?? 12;
  const limited = digits.slice(0, maxLen);
  const grouping = getGroupingForCountry(countryCode);
  let out = "";
  let i = 0;
  for (const g of grouping) {
    if (i >= limited.length) break;
    const chunk = limited.slice(i, i + g);
    if (out) out += " ";
    out += chunk;
    i += g;
  }
  // Append any remaining digits (e.g. variable-length countries)
  if (i < limited.length) {
    const rest = limited.slice(i);
    if (out) out += " ";
    out += rest;
  }
  return out;
}
